import {useMemo, useState} from "react";
import {PipelineEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getFormattedDatabaseCode} from "@ots-frontend/ots-ui";

export type UsePipelineDataTableFilterProps = Omit<UseDataTableFilterProps<PipelineEntity>, 'filterConfig'>

export function usePipelineDataTableFilter(props: UsePipelineDataTableFilterProps): UseDataTableFilterResult<PipelineEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const [categoryQuery, setCategoryQuery] = useState<string | undefined>(undefined);
  const [categoryDebouncedTypeQuery] = useDebouncedValue(categoryQuery, 200);

  const [subCategoryQuery, setSubCategoryQuery] = useState<string | undefined>(undefined);
  const [subCategoryDebouncedTypeQuery] = useDebouncedValue(subCategoryQuery, 200);

  const [allocationQuery, setAllocationQuery] = useState<string | undefined>(undefined);
  const [allocationDebouncedQuery] = useDebouncedValue(allocationQuery, 200);

  const filterConfig: DataTableFilterConfig<PipelineEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: (record, query) => !record.companyName || !record.companyName.toLowerCase().includes(query.toLowerCase())
      },
      "category": {
        setQuery: setCategoryQuery,
        query: categoryQuery,
        debouncedQuery: categoryDebouncedTypeQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.category, t)
          return !record.category.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "subCategory": {
        setQuery: setSubCategoryQuery,
        query: subCategoryQuery,
        debouncedQuery: subCategoryDebouncedTypeQuery,
        filter: (record, query) => !record.freightTypeCategory || !record.freightTypeCategory.toLowerCase().includes(query.toLowerCase())
      },
      "allocation": {
        setQuery: setAllocationQuery,
        query: allocationQuery,
        debouncedQuery: allocationDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.allocationType, t)
          return !record.allocationType.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    categoryQuery,
    categoryDebouncedTypeQuery,
    subCategoryQuery,
    subCategoryDebouncedTypeQuery,
    allocationQuery,
    allocationDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
